// This components controls answering exam questions.
// It handles navigation forward / backward and communicating with the database given a set of categories / exam type and optional timer

import {useSelectExamStats, useSelectUserProperty, waitForExamStatsToLoad} from '../firebase-listeners/firestore-user';
import {ExamQuestion, ExamStats} from '../firebase-listeners/interface';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import TopBar from './TopBar';
import AnswerForm from './AnswerForm';
import ShowImage from './ShowImage';
import CorrectAnswerBox from './CorrectAnswerBox';
import QuestionsNavigation from './QuestionsNavigation';
import {setExamStats as saveExamStatsInDB} from '../actions/firestore';
import Timer from './Timer';
import ReportRedirect from './ReportRedirect';
import {History} from 'history';
import {PreventCopyPaste} from './PreventCopyPaste';

export type Props = {
	questions: ExamQuestion[];
	examType: 'Simulation' | 'Practice';
	exam: 'CBAP' | 'CCBA' | 'ECBA' | '';
	category: string;
	shouldShowReport: boolean;
	setExamStats?: (examStats: ExamStats) => unknown;
	timerActive?: boolean;
	endButtonCTA: {
		label: string;
		title: string;
		onClick: (examKey: string) => unknown;
	};
	showAnswerCard: boolean;
	topBarTitle: string;
	restartExamUrl?: string;
	history: History;
}

export function makeExamKey(r: { exam: 'CBAP' | 'CCBA' | 'ECBA' | '', examType: 'Simulation' | 'Practice', categories: string[] }): string {
	let s = '';
	if (r.exam) {
		s = r.exam.toLowerCase().replace(/ /g, '-');
	}
	s = `${s}-${r.examType.toLowerCase().replace(/ /g, '-')}`;
	(r.categories || [])
		.map(cat => cat.replace(/ /g, '-').toLowerCase())
		.sort((a: string, b: string) => a.localeCompare(b))
		.forEach(cat => s = `${s}-${cat}`);
	return s;
}

const emptyObject = {};

export const Exam = (props: Props) => {
	const setExamStats = props?.setExamStats;
	const uid = useSelectUserProperty('uid');
	const [examKey, setExamKey] = useState<string>(makeExamKey({
		exam: props.exam,
		examType: props.examType,
		categories: [props.category],
	}));
	const examStats: ExamStats = useSelectExamStats(examKey, props.questions);
	const [showReport, setShowReport] = useState<boolean>(false);
	const [, setAtLastQuestion] = useState<boolean>(false);
	const currentQuestionId = examStats?.currentQuestionId;
	const [spinnerSuccess,] = useState<any>(undefined);
	const [answer, setAnswer] = useState<string>(examStats?.answers?.[currentQuestionId] || '');
	const [examLoading, setExamLoading] = useState<boolean>(true);
	const [showAnswerCard, setShowAnswerCard] = useState<boolean>(false);
	const markedQuestionIds = examStats?.markedQuestionIds;
	const answers = examStats?.answers || emptyObject;
	const currentQuestion = props.questions.find(q => q.id === currentQuestionId);
	const currentQuestionNumber = Math.max((examStats?.currentQuestionOrder?.indexOf(currentQuestion?.id || '') || 0), 0);

	useEffect(() => {
		if (!uid) {
			return;
		}
		waitForExamStatsToLoad(uid).finally(() => setExamLoading(false));
	}, [uid]);

	useEffect(() => {
		setExamKey(makeExamKey({exam: props.exam, examType: props.examType, categories: [props.category]}));
	}, [props.exam, props.examType, props.category]);

	useEffect(() => {
		setAnswer(answers[currentQuestionId] || '');
	}, [currentQuestionId, answers]);

	useEffect(() => {
		if (setExamStats) {
			setExamStats(examStats);
		}
	}, [examStats, props.setExamStats, setExamStats]);

	const isCurrentQuestionFlagged = useCallback(() => {
		if (!markedQuestionIds) {
			return false;
		}
		return markedQuestionIds.indexOf(currentQuestionId) >= 0;
	}, [markedQuestionIds, currentQuestionId]);

	const flagQuestion = useCallback(() => {
		const newStats = {...examStats, markedQuestionIds: [...(examStats.markedQuestionIds || []), currentQuestionId]};
		saveExamStatsInDB({uid: uid, examKey, examStats: newStats});
	}, [uid, examKey, examStats, currentQuestionId]);

	const unFlagQuestion = useCallback(() => {
		const newStats = {
			...examStats,
			markedQuestionIds: (examStats.markedQuestionIds || []).filter(qId => qId !== currentQuestionId)
		};
		saveExamStatsInDB({uid: uid, examKey, examStats: newStats});
	}, [uid, examKey, examStats, currentQuestionId]);

	const saveAnswer = useCallback((answer: string) => {
		const newStats = {...examStats, answers: {...examStats.answers, [currentQuestionId]: answer}};
		saveExamStatsInDB({uid, examKey, examStats: newStats});
	}, [currentQuestionId, examKey, examStats, uid]);

	const showCorrectAnswer = useCallback(() => {
		setShowAnswerCard(!showAnswerCard);
	}, [showAnswerCard]);

	const navigateToQuestion = useCallback((questionNumber: number) => {
		setShowAnswerCard(false);
		// Skip over questions that may no longer exist.
		let question = undefined;
		while (!question) {
			question = props.questions.find(q => q.id === examStats.currentQuestionOrder[questionNumber])
			if (!question) {
				examStats.currentQuestionOrder.splice(questionNumber, 1);
			}
		}
		
		const newStats: ExamStats = {
			...examStats,
			currentQuestionId: question.id,
		};
		setAtLastQuestion(questionNumber === props?.questions?.length-1);
		saveExamStatsInDB({uid, examKey, examStats: newStats});
	}, [examKey, examStats, props, uid]);

	const getEnunciationSections = useCallback(() => {
		// Returns a list of strings used to separate the text in a question
		return currentQuestion?.enunciation?.split('\n') || [''];
	}, [currentQuestion]);

	const Report = useMemo(() => (
		<ReportRedirect topBarTitle={props.topBarTitle} restartExamUrl={props?.restartExamUrl} exam={props?.exam} examKey={examKey} examStats={examStats} questions={props?.questions} uid={uid} category={props?.category}/>
	), [examKey, examStats, props, uid]);

	const LoadingQuestions = useMemo(() => {
		return (
			<div style={{display: "flex", justifyContent: "center", alignItems: "center", height: "75%"}}>
				<p>Loading questions...</p>
			</div>
		)
	}, []);

	const Questions = useMemo(() => {
		return (
			<div className="container">
				<TopBar history={props.history} title={props.topBarTitle}/>

				<div className="mt-3" style={{top: "20px"}}>
					<button
						data-toggle="tooltip"
						data-placement="top"
						title={props.endButtonCTA.title}
						type="button"
						className="btn btn-link"
						style={{
							float: "right",
							marginRight: "10px",
							fontSize: "18px",
							cursor: "pointer",
						}}
						onClick={(e) => {
							e.preventDefault();
							props.endButtonCTA.onClick(examKey);
						}}
					>
						{props.endButtonCTA.label}
					</button>
					<p style={{display: "inline", float: "right"}}>
						{spinnerSuccess ? spinnerSuccess : <></>}
					</p>
				</div>
				{props?.timerActive && (
					<div className="row">
						<div className="col"/>
						<div className="col">
							<Timer
								exam={props.exam}
								uid={uid}
								examKey={examKey}
							/>
						</div>
						<div className="col"/>
					</div>
				)}
				<div
					style={{
						border: "1px solid silver",
						marginTop: "70px",
						borderRadius: "10px",
					}}
					className="question-card row pb-5"
				>
					<div
						style={{height: "max-content"}}
						className="col-2 mt-4 ml-4 mb-4 exam-questionNo-box"
					>
						<h4
							className="mt-2"
							style={{fontSize: "22px", fontWeight: "normal"}}
						>
							{currentQuestionNumber + 1 + "/" + props.questions.length}
						</h4>
						{isCurrentQuestionFlagged() ? (
							// eslint-disable-next-line jsx-a11y/anchor-is-valid
							<a
								onClick={(e) => {e.preventDefault(); unFlagQuestion()}}
								role="button"
							>
								<img
									alt={"flag question"}
									src={require("../assets/images/flag-question.png")}
									style={{height: "18px", marginRight: "7px"}}
								/>
								Unmark Question
							</a>
						) : (
							// eslint-disable-next-line jsx-a11y/anchor-is-valid
							<a
								onClick={(e) => {e.preventDefault(); flagQuestion()}}
								role="button"
							>
								<img
									alt={"flag question"}
									src={require("../assets/images/flag-question.png")}
									style={{height: "18px", marginRight: "7px"}}
								/>
								Mark Question
							</a>
						)}
						<p></p>
					</div>
					<div className="col mt-4">
						<PreventCopyPaste>
							{
								getEnunciationSections().map(enunciation => {
									return <p key={enunciation} style={{fontSize: "18px"}}>
										{enunciation.replace(
											/%missing_word%/g,
											"_________"
										)}
									</p>
								})
							}
						</PreventCopyPaste>
						<p></p>
						{currentQuestion ? <AnswerForm
							onAnswerChanged={saveAnswer}
							currentQuestion={currentQuestion}
							answer={answer}
						/> : <></>}
						<ShowImage src={currentQuestion?.picture || ''}/>
						{props.showAnswerCard && (
							<button type="button"
									className="mt-4 btn btn-primary"
									style={{backgroundColor: "rgb(138, 208, 209)", borderColor: "rgb(0, 142, 143)"}}
									onClick={showCorrectAnswer}
							>
								Show Answer
							</button>
						)}
						{showAnswerCard && props.showAnswerCard ? (
							<CorrectAnswerBox
								showYourAnswer={false}
								selectedAnswer={answer || examStats?.answers?.[currentQuestionId]}
								answer={currentQuestion?.choicesArray?.[currentQuestion?.correctChoiceIndex]}
								explanation={currentQuestion?.explanation}
							/>
						) : null}
						<QuestionsNavigation
							setCurrentQuestion={navigateToQuestion}
							currentQuestionNumber={currentQuestionNumber}
							totalQuestions={props?.questions?.length || 0}
							isFromShowSimulationQuestions={props.examType === 'Simulation'}
							isFromShowQuestions={props.examType === 'Practice'}
							currentQuestionId={currentQuestionId}
							showReport={() => setShowReport(true)}
						/>
					</div>
				</div>
			</div>
		)
	}, [props, currentQuestion, spinnerSuccess, uid, examKey, currentQuestionNumber, isCurrentQuestionFlagged, getEnunciationSections, saveAnswer, answer, showCorrectAnswer, showAnswerCard, examStats, currentQuestionId, navigateToQuestion, unFlagQuestion, flagQuestion]);

	if (showReport) {
		return Report;
	}
	if (!props?.questions?.length || examLoading) {
		return LoadingQuestions;
	}
	return Questions;
}
